import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Identity } from 'models';

import {
    Card,
    Dropdown,
    DropdownItem,
    DropdownSort,
    FilterSetController,
    Table,
    TableCell,
    TableBody,
    TableRow,
    TableCellDropdown,
    ToastContext,
    LoaderCard
} from '@weezevent/nacre';

import { InfoTextTooltip, SauronEmptyCard } from 'components';

import { matchSearch } from 'utils/utils';

import css from '../../emails.module.css';

export const ActiveDomains = ({ domain, syncDomain }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [ordering, setOrdering] = React.useState('id');
    const [search, setSearch] = React.useState('');

    const sortedOrganizations = React.useMemo(() => {
        let organizations = [...(domain.organizations ?? [])];

        if (search) {
            organizations = organizations.filter(orga => matchSearch([orga.id, orga.name], search));
        }

        switch (ordering) {
            case 'name':
                organizations.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case '-name':
                organizations.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'id':
                organizations.sort((a, b) => a.id - b.id);
                break;
            case '-id':
                organizations.sort((a, b) => b.id - a.id);
                break;
            default:
                return organizations;
        }

        return organizations;
    }, [domain.organizations, ordering, search]);

    const sortByIds = [
        { key: 'id', label: t('sauron.emails.domains.sort.id') },
        { key: '-id', label: t('sauron.emails.domains.sort.-id') }
    ];
    const sortByNames = [
        { key: 'name', label: t('sauron.emails.domains.sort.a-z') },
        { key: '-name', label: t('sauron.emails.domains.sort.z-a') }
    ];

    return (
        <Card>
            <div className={css['card-header']}>
                <FilterSetController
                    className={css['filter-block']}
                    total={domain.organizations.length}
                    labelTotal={t('sauron.organizations.organization', { count: domain.organizations.length })}
                    onChange={() => {}}
                    search={{
                        onChange: (_, value) => setSearch(value),
                        handleReset: () => setSearch(''),
                        placeholder: t('sauron.emails.domains.search-organizations')
                    }}
                />
            </div>
            {sortedOrganizations.length > 0 ? (
                <Table>
                    <Table.THead>
                        <Table.Tr>
                            <TableCell>
                                <DropdownSort
                                    active={['id', '-id'].includes(ordering)}
                                    label={t(`common.id`)}
                                    name="sort"
                                    ordering={ordering}
                                    items={sortByIds.map(item => (
                                        <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                    ))}
                                />
                            </TableCell>
                            <TableCell>
                                <DropdownSort
                                    active={['name', '-name'].includes(ordering)}
                                    label={t(`common.name`)}
                                    name="sort"
                                    ordering={ordering}
                                    items={sortByNames.map(item => (
                                        <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                    ))}
                                />
                            </TableCell>
                            {domain?.is_success && <TableCell>{t(`sauron.emails.domains.actions.label`)}</TableCell>}
                        </Table.Tr>
                    </Table.THead>
                    {domain.is_success &&
                        sortedOrganizations.map((organization, index) => (
                            <TableBody key={index}>
                                <TableRow>
                                    <TableCell>
                                        <span className={css['organization-id']}>O{organization.weez_orga_id}</span>
                                    </TableCell>
                                    <TableCell>{organization.name || t('common.unnamed')}</TableCell>

                                    <TableCellDropdown>
                                        <Dropdown.Item
                                            key={0}
                                            item={t('sauron.emails.domains.actions.see-domains')}
                                            onClick={() => history.push(`/organizations/O${organization.id}/emails/domains`)}
                                        />
                                        <RemoveOrgaDropDown organizationId={organization.id} domain={domain} syncDomain={syncDomain} />
                                    </TableCellDropdown>
                                </TableRow>
                            </TableBody>
                        ))}
                </Table>
            ) : (
                <SauronEmptyCard subTitle={t('sauron.emails.domains.domain-info.no-organizations')} />
            )}
        </Card>
    );
};

const RemoveOrgaDropDown = ({ organizationId, domain, syncDomain }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [identities, loadingIdentities] = Identity.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId]
    );
    const handleDelete = React.useCallback(() => {
        domain.id = domain.name;
        domain.delete({ organizationId }).then(() => syncDomain());
    }, [domain, organizationId]);
    const isDomainUsed = domain?.isUsed(identities);
    return loadingIdentities ? (
        <Dropdown.Item key="loading">
            <div className={css['loader-wrapper']}>
                <LoaderCard active />
            </div>
        </Dropdown.Item>
    ) : (
        <InfoTextTooltip className={css['action-delete-tooltip']} title={t('sauron.emails.domains.actions.remove-domain.tool-tip')} tooltip={isDomainUsed}>
            <Dropdown.Item key={1} item={t('sauron.emails.domains.actions.remove-organization')} deleteItem onClick={handleDelete} disabled={isDomainUsed} />
        </InfoTextTooltip>
    );
};
